import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';
import { ScheduleReportService } from '../schedules-report.service';
import { ConfirmationModalComponent } from 'src/app/components/confirmation-modal/confirmation-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AppConstants } from 'src/app/app.constants';

const momentFn: any = moment;

@Component({
  selector: 'app-schedules-report',
  templateUrl: './schedules-report.component.html',
  styleUrls: ['./schedules-report.component.scss']
})
export class SchedulesReportComponent implements OnInit {
  currentDate = momentFn(Date.now()).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
  startDate: string = this.currentDate.toISOString()
  endDate: string = this.currentDate.add(13, "days").toISOString()
  searchString: string = ""
  showLoader: boolean = false
  activeStep: number = 1;
  source: any[] = [];
  target: any[] = [];
  qualifiers: Qualifier[] = []
  selectedQualifiers: Qualifier[] = []
  selectedChannelValues: string[] = [];
  reportChannels: ChannelName[] = [];
  selectAll = false;
  selectedChannels: ChannelName[] = [];
  selectedPlatform: string[] = ['OTT', 'DTH', 'Both'];
  activePlatformFilter = 'ALL';

  constructor(
    private scheduleReportService: ScheduleReportService,
    private modalService: NgbModal
  ) {

  }


  ngOnInit(): void {
    this.initializeAvailableFields();
    this.initializeQualifiers();
    this.showLoader = false
    this.getReportChannels(this.searchString)
  }

  checkRequireFields() {
    return (this.startDate == null || this.endDate == null || this.selectedChannelValues?.length < 1);
  }

  getReportChannels(search: string) {
    this.showLoader = true;
    this.selectAll = false;
    this.reportChannels = [];
    this.updateSelectedChannels();
    this.scheduleReportService.getReportChannels(search, this.selectedPlatform).subscribe(res => {
      this.showLoader = false;
      res.forEach(item => {
        if (item.channelNames.length > 0) {
          const tmpChannel: ChannelName = item.channelNames[0];
          tmpChannel.id = item.id;
          this.reportChannels.push(tmpChannel);
        }
      });
    });
  }

  setPlatformFilter(platform: 'ALL' | 'OTT' | 'DTH') {
    this.activePlatformFilter = platform;
    this.selectedPlatform = platform === 'ALL'
      ? ['OTT', 'DTH', 'Both']
      : platform === 'OTT'
        ? ['OTT', 'Both']
        : ['DTH', 'Both'];
    this.getReportChannels(this.searchString);
  }

  onChannelChecked(channel: any) {
    if (channel.isChecked) {
      this.selectedChannels.push(channel);
    } else {
      const index = this.selectedChannels.indexOf(channel);
      if (index > -1) {
        this.selectedChannels.splice(index, 1);
      }
    }
    this.updateSelectedChannels();
  }

  updateSelectedChannels() {
    this.selectedChannels = this.reportChannels.filter(channel => channel.isChecked);
    this.selectedChannelValues = this.selectedChannels?.map((channel) => channel.id);
  }

  toggleSelectAll() {
    this.reportChannels.forEach(channel => channel.isChecked = this.selectAll);
    if (this.selectAll == true) {
      this.selectedChannels = this.reportChannels;
    } else {
      this.selectedChannels = [];
    }
    this.updateSelectedChannels();
  }

  initializeAvailableFields() {
    this.scheduleReportService.getAvailableFields().subscribe(res => {
      this.source = res.availableFields;
      this.target = res.defaultFields;
      this.reorderLists();
    })
  }

  initializeQualifiers() {
    this.scheduleReportService.getQualifiers().subscribe(res => {
      this.qualifiers = res.response.map(item => {
        const newName = this.splitAndCapitalizeName(item.name.slice(12)); // Remove "epgQualifier" prefix
        return { ...item, showName: newName };
      });
      const noneValue: Qualifier = {
        id: "none",
        name: "None",
        description: "none",
        showName: "None",
      };
      this.selectedQualifiers = this.qualifiers;
      this.selectedQualifiers.splice(0, 0, noneValue);
    });
  }

  onListChange() {
    this.reorderLists();
  }

  reorderLists() {
    this.source.sort((a, b) => a.order - b.order);
    this.target.sort((a, b) => a.order - b.order);
  }

  splitAndCapitalizeName(name: string): string {
    let processedName = name[0];
    const regex = /([A-Z])(?=[^A-Z])/g;
    processedName += name.slice(1).replace(regex, ' $1');
    return processedName;
  }

  onClickNext() {
    this.activeStep = 2;
  }

  onClickBack() {
    this.activeStep = 1;
  }

  onDateChange(isStartDate: boolean) {
    if (isStartDate) {
      this.startDate = momentFn(this.startDate).utcOffset(0, true).toISOString();
    } else {
      this.endDate = momentFn(this.endDate).utcOffset(0, true).toISOString();
    }
  }

  onClickExport() {
    this.showLoader = true;
    const qualifiers: string[] = this.selectedQualifiers.map((item) => item.name);
    const exportFields: string[] = this.target.map((item) => item.displayName);
    const excludedChannelValues: string[] = this.reportChannels
      .filter((channel) => !channel.isChecked)
      .map((channel) => channel.id);
    const useAllExceptMode = this.selectAll;

    const request: ExportScheduleReportRequestDto = {
      startDate: this.startDate.split("T")[0],
      endDate: this.endDate.split("T")[0],
      qualifiers: qualifiers,
      channelIds: useAllExceptMode ? [] : this.selectedChannelValues,
      channelSelectionMode: useAllExceptMode ? 'ALL_EXCEPT' : 'INCLUDE',
      excludedChannelIds: useAllExceptMode ? excludedChannelValues : [],
      exportFields: exportFields,
    }

    const shouldUseAsyncMode = useAllExceptMode || this.selectedChannelValues.length >= 10;
    if (shouldUseAsyncMode) {
      this.scheduleReportService.generateReportAsync(request).subscribe({
        next: (jobResponse) => {
          this.showLoader = false;
          this.showBackgroundReportMessage();
          this.pollAsyncReport(jobResponse.jobId);
        },
        error: () => {
          this.showLoader = false;
          this.showAlert(AppConstants.ERROR, 'Failed to queue the report.');
        },
      });
      return;
    }

    this.scheduleReportService.generateReport(request).subscribe({
      next: (response) => {
        this.showLoader = false;
        const blob = new Blob([response.body as BlobPart], { type: 'application/json' });
        const contentDisposition = response.headers.get('content-disposition') ?? 'attachment';
        const fileName = contentDisposition
          .split(';')[1]
          ?.replace(/^filename=/, '')
          ?.trim() ?? 'schedule-report.json';

        this.downloadFile(blob, fileName);
        this.showAlert(AppConstants.SUCCESS, `File ${fileName} is generated successfully.`);
      },
      error: () => {
        this.showLoader = false;
        this.showAlert(AppConstants.ERROR, 'Failed to generate the report.');
      },
    });
  }

  private pollAsyncReport(jobId: string) {
    this.scheduleReportService.getReportStatus(jobId).subscribe({
      next: (statusResponse) => {
        if (statusResponse.status === 'completed') {
          if (statusResponse.presignedUrl) {
            this.showLoader = false;
            const fileName = statusResponse.fileName ?? 'schedule-report.json';
            this.showAlert(
              AppConstants.SUCCESS,
              `File ${fileName} is generated successfully.`,
              () => this.downloadFromPresignedUrl(statusResponse.presignedUrl as string, fileName)
            );
            return;
          }

          this.scheduleReportService.downloadReport(jobId).subscribe({
            next: (downloadResponse) => {
              this.showLoader = false;
              if (downloadResponse.presignedUrl) {
                const fileName = downloadResponse.fileName ?? 'schedule-report.json';
                this.showAlert(
                  AppConstants.SUCCESS,
                  `File ${fileName} is generated successfully.`,
                  () => this.downloadFromPresignedUrl(downloadResponse.presignedUrl as string, fileName)
                );
                return;
              }

              this.showAlert(AppConstants.ERROR, 'Generated report is not available for download yet.');
            },
            error: () => {
              this.showLoader = false;
              this.showAlert(AppConstants.ERROR, 'Failed to download the generated report.');
            },
          });
          return;
        }

        if (statusResponse.status === 'failed') {
          this.showLoader = false;
          this.showAlert(AppConstants.ERROR, statusResponse.error ?? 'Failed to generate the report.');
          return;
        }

        setTimeout(() => this.pollAsyncReport(jobId), 2000);
      },
      error: () => {
        this.showLoader = false;
        this.showAlert(AppConstants.ERROR, 'Failed to retrieve report status.');
      },
    });
  }

  private showBackgroundReportMessage() {
    this.showAlert(
      'Info',
      'Your report is being generated in the background. You can leave this screen and the file will download when it is ready.',
      undefined,
      false
    );
  }

  private downloadFile(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();

    // Clean up the temporary URL (optional, but recommended to avoid memory leaks)
    window.URL.revokeObjectURL(url);
  }

  private downloadFromPresignedUrl(url: string, fileName: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener';
    link.click();
  }

  showAlert(title: string, message: string, onConfirm?: () => void, shouldReset: boolean = true) {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      size: "md",
      centered: true,
      backdrop: "static",
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.isAlert = true;
    modalRef.result.then((res) => {
      if (res) {
        if (onConfirm) {
          onConfirm();
        }
        if (shouldReset) {
          this.reset();
        }
      }
    })
  }

  reset() {
    this.activeStep = 1
    this.searchString = ""
    this.showLoader = false
    this.currentDate = momentFn(Date.now()).set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
    this.startDate = this.currentDate.toISOString()
    this.endDate = this.currentDate.add(13, "days").toISOString()
    this.initializeAvailableFields()
    this.initializeQualifiers()
    this.getReportChannels(this.searchString)
  }
}
